import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PrintSingleDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentTitle: string;
  children: ReactNode;
}

export default function PrintSingleDocumentModal({
  isOpen,
  onClose,
  documentTitle,
  children,
}: PrintSingleDocumentModalProps) {
  if (!isOpen) return null;

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 600);
  };

  return (
    <>
      {/* Modal Overlay — caché à l'impression */}
      <div className="no-print fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/50 backdrop-blur-sm p-4">
        <div className="my-8 w-full max-w-3xl bg-white rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-teal-600 to-teal-700 rounded-t-2xl px-6 py-4 flex items-center justify-between">
            <h2 className="text-lg font-bold text-white flex items-center gap-3">
              <i className="ri-printer-line text-xl w-5 h-5 flex items-center justify-center"></i>
              {documentTitle}
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 text-white transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Document Preview — visible à l'écran */}
          <div className="p-4">
            <div className="print-preview-wrapper max-h-[60vh] overflow-auto rounded-lg">
              {children}
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-sm cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-arrow-go-back-line"></i>
              Fermer
            </button>
            <button
              onClick={handlePrint}
              className="px-8 py-2.5 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:from-teal-700 hover:to-teal-800 transition-all font-semibold text-sm shadow-lg cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-printer-line"></i>
              Imprimer ce document
            </button>
          </div>
        </div>
      </div>

      {/* Contenu d'impression — rendu via Portal directement dans <body> pour l'isoler du layout de la page */}
      {createPortal(
        <div className="print-container">
          {children}
        </div>,
        document.body
      )}
    </>
  );
}